import { cx } from 'cva';
import {
  type FunctionComponent,
  useCallback,
  useEffect,
  useEffectEvent,
  useState,
} from 'react';
import { useInView } from 'react-intersection-observer';
import ReactMarkdown from 'react-markdown';
import { AlertDialog } from '../../atoms/AlertDialog/AlertDialog';
import { Button } from '../../atoms/Button/Button';
import { Checkbox } from '../../atoms/Checkbox/Checkbox';
import { useTranslation } from '../../i18n/resources';
import styles from './UserAgreementAlertDialog.module.css';

export type UserAgreementAlertDialogProps = {
  onClose: () => void;
  onSubmit: () => void;
  open: boolean;
};

export const UserAgreementAlertDialog: FunctionComponent<
  UserAgreementAlertDialogProps
> = ({ onClose, onSubmit, open }) => {
  const { renderTrans, t } = useTranslation();

  const [userScrolledAgreement, setUserScrolledAgreement] =
    useState<boolean>(false);
  const [agreementChecked, setAgreementChecked] = useState<boolean>(false);

  const handleCloseAlertDialog = useCallback((): void => {
    onClose();
  }, [onClose]);

  const handleAgreementChecked = useCallback((): void => {
    setAgreementChecked(!agreementChecked);
  }, [agreementChecked]);

  const handleSubmit = (): void => {
    onSubmit();
  };

  const { inView, ref: bottomRef } = useInView({
    root: null,
    rootMargin: '0px',
    threshold: 0,
    triggerOnce: true,
  });

  const handleInViewChange = useEffectEvent((scrolledIntoView: boolean) => {
    if (scrolledIntoView && !userScrolledAgreement) {
      setUserScrolledAgreement(true);
    }
  });

  useEffect(() => {
    handleInViewChange(inView);
  }, [inView]);

  return (
    <AlertDialog
      footer={
        <>
          <div className={styles.agreeRow}>
            <Checkbox
              checked={agreementChecked}
              disabled={!userScrolledAgreement}
              onClick={handleAgreementChecked}
            />
            <p
              className={cx(
                styles.agreementCheckboxText,
                !userScrolledAgreement && styles.checkboxDisabled,
              )}
              onClick={() => {
                if (userScrolledAgreement) {
                  handleAgreementChecked();
                }
              }}
            >
              {renderTrans('userAgreement', 'footer_agreement', {
                components: {
                  red: <span style={{ color: 'red' }} />,
                },
              })}
            </p>
          </div>
          <div className={styles.actionButtons}>
            <Button intent="secondary" onClick={handleCloseAlertDialog}>
              {t('common', 'cancel')}
            </Button>
            <Button disabled={!agreementChecked} onClick={handleSubmit}>
              {t('common', 'submit')}
            </Button>
          </div>
        </>
      }
      onClose={handleCloseAlertDialog}
      open={open}
      padding="large"
      size="large"
      title={t('userAgreement', 'title')}
    >
      <div className={styles.userAgreementText}>
        <ReactMarkdown>
          {t('userAgreement', 'user_agreement_markdown')}
        </ReactMarkdown>
        <div className={styles.bottomRef} ref={bottomRef} />
      </div>
    </AlertDialog>
  );
};
